import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type Ref,
} from 'react';
import { Image, Palette, Paperclip, Upload, X, type LucideIcon } from 'lucide-react';
import {
  ATTACHMENT_MAX_BYTES,
  type Attachment,
  type CardDetail,
  type CoverColour,
  type CoverSize,
  type CoverTextTreatment,
} from '@molt/shared';
import { Button, IconButton, Notice, Popover } from '@/components';
import { announce } from '@/lib/announce';
import {
  formatAttachmentSize,
  presignAttachmentUpload,
  putAttachmentBytes,
  useCreateCardAttachment,
} from '@/lib/attachments';
import {
  attachmentCoverPut,
  colourCoverPut,
  coverColourOptions,
  coverPhotoOptions,
  coverVisual,
  photoLibraryCoverPut,
} from '@/lib/card-cover';
import { useRemoveCardCover, useSetCardCover } from '@/lib/cards';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface CardCoverSectionProps {
  card: CardDetail;
  boardId: string;
  canEdit: boolean;
  /**
   * Render ONLY the trigger control, for the card back's "Add to card" row
   * (`docs/design/stage2/card-back.md` D1): no heading, no empty-state prose.
   * The trigger keeps its accessible name and its popover is unchanged. A
   * viewer who cannot edit gets nothing, because an empty section with no
   * control in it says nothing the card back needs to say.
   */
  compact?: boolean | undefined;
  /**
   * The trigger, so the card back can focus it when this section first moves
   * into the body because the member just filled it from its compact trigger
   * in the "Add to card" row, which unmounted with focus inside it.
   */
  triggerRef?: Ref<HTMLButtonElement> | undefined;
}

export function CardCoverSection({
  card,
  boardId,
  canEdit,
  compact = false,
  triggerRef = null,
}: CardCoverSectionProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const setCover = useSetCardCover();
  const removeCover = useRemoveCardCover();
  const createAttachment = useCreateCardAttachment();
  const coverFileInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<'colour' | 'photo_library' | 'attachment'>(
    card.cover?.source ?? 'colour',
  );
  const [colour, setColour] = useState<CoverColour>(
    card.cover?.source === 'colour' ? (card.cover.colour as CoverColour) : 'green',
  );
  const [photoRef, setPhotoRef] = useState(
    card.cover?.source === 'photo_library'
      ? card.cover.photoRef
      : (coverPhotoOptions(t)[0]?.photoRef ?? ''),
  );
  const [attachmentId, setAttachmentId] = useState(
    card.cover?.source === 'attachment' ? card.cover.attachmentId : '',
  );
  const [size, setSize] = useState<CoverSize>(card.cover?.size ?? 'normal');
  const [textTreatment, setTextTreatment] = useState<CoverTextTreatment>(
    card.cover?.textTreatment ?? 'dark',
  );
  const [selectedCoverFile, setSelectedCoverFile] = useState<File | null>(null);
  const [coverUploadError, setCoverUploadError] = useState<string | null>(null);
  const [coverUploadBusy, setCoverUploadBusy] = useState(false);
  const [uploadedAttachments, setUploadedAttachments] = useState<Attachment[]>([]);
  const [coverUploadAnnouncement, setCoverUploadAnnouncement] = useState('');
  const title = card.title.trim() === '' ? t('cardBack.untitledCard') : card.title;
  // FE-80.1 card-cover-tail. `t` threaded into all three, and into the two
  // `useMemo` dependency arrays with it: `useInterfaceLanguage`'s `t` is
  // memoised on the resolved catalogue (`interface-language.tsx:15529`), so
  // `[t]` recomputes these only when the catalogue itself changes, not per
  // render. ⚠ The board front (`CardTile.tsx:933`) threads the SAME translator
  // into the SAME helper; fixing one caller alone leaves the other in English.
  const visual = coverVisual(t, card.cover);
  const colours = useMemo(() => coverColourOptions(t), [t]);
  const photos = useMemo(() => coverPhotoOptions(t), [t]);
  const fileAttachments = useMemo(() => {
    const existing = card.attachments.filter((attachment) => attachment.kind === 'file');
    const existingIds = new Set(existing.map((attachment) => attachment.id));
    const optimistic = uploadedAttachments.filter(
      (attachment) => attachment.kind === 'file' && !existingIds.has(attachment.id),
    );
    return [...optimistic, ...existing];
  }, [card.attachments, uploadedAttachments]);
  const uploadBusy = coverUploadBusy || createAttachment.isPending;
  const selectedCoverFileTooLarge =
    selectedCoverFile !== null && selectedCoverFile.size > ATTACHMENT_MAX_BYTES;
  const selectedCoverFileInvalid =
    selectedCoverFile !== null && !isCoverImageFile(selectedCoverFile);
  const selectedCoverFileName = selectedCoverFile?.name ?? '';
  const uploadDisabled =
    selectedCoverFile === null ||
    selectedCoverFileTooLarge ||
    selectedCoverFileInvalid ||
    uploadBusy;
  const canApplyCover =
    source === 'photo_library'
      ? photoRef !== ''
      : source === 'attachment'
        ? attachmentId !== ''
        : true;
  const applyDisabledReason = uploadBusy
    ? t('cardBack.cover.disabled.uploadFirst')
    : source === 'photo_library' && photoRef === ''
      ? t('cardBack.cover.disabled.photoFirst')
      : source === 'attachment' && attachmentId === ''
        ? t('cardBack.cover.disabled.attachmentFirst')
        : undefined;

  useEffect(() => {
    if (!open) return;
    setSource(card.cover?.source ?? 'colour');
    setColour(
      card.cover?.source === 'colour' ? (card.cover.colour as CoverColour) : 'green',
    );
    setPhotoRef(
      card.cover?.source === 'photo_library'
        ? card.cover.photoRef
        : (photos[0]?.photoRef ?? ''),
    );
    setAttachmentId(card.cover?.source === 'attachment' ? card.cover.attachmentId : '');
    setSize(card.cover?.size ?? 'normal');
    setTextTreatment(card.cover?.textTreatment ?? 'dark');
    setSelectedCoverFile(null);
    setCoverUploadError(null);
    setCoverUploadAnnouncement('');
    if (coverFileInputRef.current !== null) coverFileInputRef.current.value = '';
  }, [card.cover, open, photos]);

  useEffect(() => {
    setUploadedAttachments([]);
  }, [card.id]);

  async function submitCoverUpload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const file = selectedCoverFile;
    if (file === null || uploadBusy) return;

    if (!isCoverImageFile(file)) {
      setCoverUploadError(t('cardBack.cover.upload.unsupportedType'));
      return;
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setCoverUploadError(t('cardBack.cover.upload.tooLarge'));
      return;
    }

    setCoverUploadError(null);
    setCoverUploadBusy(true);
    try {
      const presign = await presignAttachmentUpload(card.id, {
        fileName: file.name,
        sizeBytes: file.size,
        contentType: file.type || 'application/octet-stream',
      });
      await putAttachmentBytes(t, presign, file);
      const attachment = await createAttachment.mutateAsync({
        cardId: card.id,
        boardId,
        body: {
          kind: 'file',
          storageKey: presign.storageKey,
          displayName: file.name,
        },
      });
      setUploadedAttachments((previous) => [
        attachment,
        ...previous.filter((candidate) => candidate.id !== attachment.id),
      ]);
      setAttachmentId(attachment.id);
      setSource('attachment');
      setSelectedCoverFile(null);
      setCoverUploadAnnouncement(
        t('cardBack.cover.upload.complete', { name: attachment.displayName }),
      );
      if (coverFileInputRef.current !== null) coverFileInputRef.current.value = '';
    } catch (cause) {
      setCoverUploadError(
        cause instanceof Error ? cause.message : t('cardBack.cover.error.uploadFailed'),
      );
    } finally {
      setCoverUploadBusy(false);
    }
  }

  function applyCover() {
    if (!canApplyCover || uploadBusy) return;

    const cover =
      source === 'colour'
        ? colourCoverPut({ colour, size, textTreatment })
        : source === 'photo_library'
          ? photoLibraryCoverPut({
              photoRef,
              photoAttribution:
                photos.find((photo) => photo.photoRef === photoRef)?.attribution ?? null,
              size,
              textTreatment,
            })
          : attachmentCoverPut({ attachmentId, size, textTreatment });

    setCover.mutate(
      { cardId: card.id, boardId, cover },
      {
        onSuccess: () => {
          announce(t('cardBack.cover.announce.saved'));
          setOpen(false);
        },
      },
    );
  }

  function clearCover() {
    removeCover.mutate(
      { cardId: card.id, boardId },
      {
        onSuccess: () => {
          announce(t('cardBack.cover.announce.removed'));
          setOpen(false);
        },
      },
    );
  }

  const editor = canEdit ? (
    <Popover
      open={open}
      onOpenChange={setOpen}
      title={t('cardBack.cover.popoverTitle')}
      width={360}
      trigger={
        <Button
          ref={triggerRef}
          type="button"
          size="sm"
          variant="secondary"
          aria-label={t('cardBack.cover.triggerLabel', { title })}
          iconStart={<Palette aria-hidden="true" className="size-4" />}
        >
          {t('cardBack.cover.trigger')}
        </Button>
      }
      closeControl={
        <IconButton label={t('cardBack.cover.closePopover')} size="sm">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <div className="flex w-full flex-wrap justify-between gap-2">
          <Button
            type="button"
            size="sm"
            variant="quiet"
            disabled={card.cover === null || removeCover.isPending}
            busy={removeCover.isPending}
            busyLabel={t('cardBack.cover.removing')}
            onClick={clearCover}
          >
            {t('cardBack.cover.remove')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={!canApplyCover || uploadBusy}
            {...(applyDisabledReason === undefined
              ? {}
              : { disabledReason: applyDisabledReason })}
            busy={setCover.isPending}
            busyLabel={t('cardBack.cover.saving')}
            onClick={applyCover}
          >
            {t('cardBack.cover.apply')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Segmented
          label={t('cardBack.cover.source.label')}
          options={[
            {
              value: 'colour',
              label: t('cardBack.cover.source.colour'),
              icon: Palette,
            },
            {
              value: 'photo_library',
              label: t('cardBack.cover.source.photo'),
              icon: Image,
            },
            {
              value: 'attachment',
              label: t('cardBack.cover.source.attachment'),
              icon: Paperclip,
            },
          ]}
          value={source}
          onChange={setSource}
        />

        {source === 'colour' ? (
          <div
            className="grid grid-cols-5 gap-2"
            aria-label={t('cardBack.cover.coloursLabel')}
          >
            {colours.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-label={option.label}
                aria-pressed={colour === option.value}
                style={option.style}
                className={cn(
                  'h-9 rounded-card border border-border-strong text-xs font-medium',
                  option.inkClassName,
                  colour === option.value && 'outline outline-2 outline-accent',
                )}
                onClick={() => setColour(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}

        {source === 'photo_library' ? (
          <div
            className="grid grid-cols-1 gap-2"
            aria-label={t('cardBack.cover.photosLabel')}
          >
            {photos.map((option) => (
              <button
                key={option.photoRef}
                type="button"
                aria-pressed={photoRef === option.photoRef}
                className={cn(
                  'min-h-14 rounded-card border border-border-strong bg-cover bg-center px-3 py-2 text-left text-sm font-medium text-board-ink-light',
                  photoRef === option.photoRef && 'outline outline-2 outline-accent',
                )}
                style={option.style}
                onClick={() => setPhotoRef(option.photoRef)}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}

        {source === 'attachment' ? (
          <div
            className="flex flex-col gap-2"
            aria-label={t('cardBack.cover.attachmentsLabel')}
          >
            <form
              className="flex flex-col gap-3 rounded-card border border-border bg-surface-raised p-3"
              onSubmit={(event) => void submitCoverUpload(event)}
            >
              <label className="flex flex-col gap-1 text-sm font-medium text-ink">
                {t('cardBack.cover.upload.fileLabel')}
                <input
                  ref={coverFileInputRef}
                  type="file"
                  accept="image/*"
                  className="block w-full rounded-card border border-border-strong bg-surface px-3 py-2 text-sm text-ink file:mr-3 file:rounded-card file:border-0 file:bg-accent file:px-2 file:py-1 file:text-sm file:font-medium file:text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                  onChange={(event) => {
                    const next = event.currentTarget.files?.[0] ?? null;
                    setSelectedCoverFile(next);
                    setCoverUploadError(null);
                  }}
                />
              </label>
              {selectedCoverFileInvalid ? (
                <p className="text-sm text-bad">
                  {t('cardBack.cover.upload.unsupportedType')}
                </p>
              ) : null}
              {selectedCoverFileTooLarge ? (
                <p className="text-sm text-bad">{t('cardBack.cover.upload.tooLarge')}</p>
              ) : null}
              {coverUploadError !== null ? (
                <p className="text-sm text-bad">{coverUploadError}</p>
              ) : null}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-ink-muted">
                  {selectedCoverFile === null
                    ? t('cardBack.cover.upload.noneSelected')
                    : t('cardBack.cover.upload.selection', {
                        name: selectedCoverFileName,
                        size: formatAttachmentSize(t, selectedCoverFile.size),
                      })}
                </p>
                <Button
                  type="submit"
                  size="sm"
                  variant="primary"
                  busy={uploadBusy}
                  busyLabel={t('cardBack.cover.upload.busy')}
                  disabled={uploadDisabled}
                  iconStart={<Upload aria-hidden="true" className="size-4" />}
                >
                  {t('cardBack.cover.upload.submit')}
                </Button>
              </div>
              {uploadBusy ? (
                <div
                  role="progressbar"
                  aria-label={t('cardBack.cover.upload.progress')}
                  aria-valuetext={t('cardBack.cover.upload.busy')}
                  className="h-1.5 overflow-hidden rounded-full bg-border"
                >
                  <div className="h-full w-1/2 rounded-full bg-accent" />
                </div>
              ) : null}
            </form>
            {fileAttachments.length === 0 ? (
              <Notice
                tone="info"
                title={t('cardBack.cover.attachments.emptyTitle')}
                body={t('cardBack.cover.attachments.emptyBody')}
              />
            ) : (
              fileAttachments.map((attachment) => (
                <button
                  key={attachment.id}
                  type="button"
                  aria-pressed={attachmentId === attachment.id}
                  className={cn(
                    'rounded-card border border-border-strong bg-surface px-3 py-2 text-left text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
                    attachmentId === attachment.id && 'outline outline-2 outline-accent',
                  )}
                  onClick={() => setAttachmentId(attachment.id)}
                >
                  {attachment.displayName}
                </button>
              ))
            )}
          </div>
        ) : null}

        <Segmented
          label={t('cardBack.cover.size.label')}
          options={[
            { value: 'normal', label: t('cardBack.cover.size.normal') },
            { value: 'full', label: t('cardBack.cover.size.full') },
          ]}
          value={size}
          onChange={setSize}
        />
        <Segmented
          label={t('cardBack.cover.textTreatment.label')}
          options={[
            { value: 'dark', label: t('cardBack.cover.textTreatment.dark') },
            { value: 'light', label: t('cardBack.cover.textTreatment.light') },
          ]}
          value={textTreatment}
          onChange={setTextTreatment}
        />
      </div>
    </Popover>
  ) : null;

  if (compact) {
    return editor === null ? null : (
      <>
        {editor}
        <div role="status" aria-live="polite" className="sr-only">
          {coverUploadAnnouncement}
        </div>
      </>
    );
  }

  return (
    <section aria-labelledby="card-cover-heading" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="card-cover-heading" className="text-sm font-semibold text-ink">
          {t('cardBack.cover.heading')}
        </h2>
        <div className="min-w-0 flex-1" />
        {editor}
      </div>

      {visual === null ? (
        <p className="text-sm text-ink-muted">{t('cardBack.cover.noCover')}</p>
      ) : (
        <div
          aria-label={visual.label}
          className={cn(
            'flex h-16 items-end rounded-card border border-border-strong bg-board bg-cover bg-center p-2 text-sm font-medium',
            visual.inkClassName,
          )}
          style={visual.style}
        >
          {visual.label}
        </div>
      )}
      <div role="status" aria-live="polite" className="sr-only">
        {coverUploadAnnouncement}
      </div>
    </section>
  );
}

const COVER_IMAGE_EXTENSIONS = new Set([
  'apng',
  'avif',
  'bmp',
  'gif',
  'jpeg',
  'jpg',
  'png',
  'svg',
  'webp',
]);

function isCoverImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  const extension = file.name.split('.').at(-1)?.toLowerCase();
  return extension !== undefined && COVER_IMAGE_EXTENSIONS.has(extension);
}

function Segmented<TValue extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly {
    value: TValue;
    label: string;
    icon?: LucideIcon;
  }[];
  value: TValue;
  onChange: (value: TValue) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-xs font-medium text-ink-muted">{label}</legend>
      <div className="grid grid-cols-2 gap-1 rounded-card bg-surface p-1">
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={value === option.value}
              className={cn(
                'inline-flex items-center justify-center gap-1.5 rounded-[6px] px-2 py-1.5 text-sm font-medium text-ink-muted',
                value === option.value && 'bg-surface-raised text-ink shadow-card',
              )}
              onClick={() => onChange(option.value)}
            >
              {Icon === undefined ? null : (
                <Icon aria-hidden="true" className="size-3.5" />
              )}
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
